import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { format, parseISO, differenceInMinutes } from "date-fns";
import api from "../api/client";
import type { ConversationEvent } from "../types";
import { MessageCard } from "../components/MessageCard";

const POLL_INTERVAL = 5000; // 5 seconds

interface ShareData {
  share: {
    title: string | null;
    description: string | null;
    scope_type: string;
    created_at: string;
    owner: {
      username: string;
      avatar_url: string | null;
    };
  };
  content: {
    events: ConversationEvent[];
    total: number;
    session_id?: string;
    git_remote_url?: string;
    file_name?: string;
  };
}

export function SharedView() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ShareData | null>(null);
  useEffect(() => {
    document.title = data?.share?.title
      ? `vibe-check ⏵${data.share.title}`
      : "vibe-check";
  }, [data]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [copied, setCopied] = useState(false);
  const [messageText, setMessageText] = useState("");
  const limit = 500;
  const latestEventId = useRef<number | null>(null);

  const shareUrl = `${window.location.origin}/s/${token}`;
  const agentPrompt = `Fetch ${shareUrl}.txt and catch up on this conversation`;

  const copyPrompt = useCallback(() => {
    navigator.clipboard.writeText(agentPrompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [agentPrompt]);

  useEffect(() => {
    if (token) {
      loadContent(true);
    }
  }, [token]);

  // Poll for new messages (for live/recent sessions)
  useEffect(() => {
    if (!token || error) return;

    const pollForNewMessages = async () => {
      try {
        // For ASC-ordered content, we need to check if there are new messages
        // by comparing total count and fetching new messages at the end
        if (!data || latestEventId.current === null) return;

        // Fetch from current offset to get any new messages
        const currentCount = data.content.events.length;
        const result = await api.getSharedContent(token, limit, currentCount);

        if (result.content.events.length > 0) {
          const existingIds = new Set(data.content.events.map((e) => e.id));
          const uniqueNewEvents = result.content.events.filter(
            (e) => !existingIds.has(e.id),
          );

          if (uniqueNewEvents.length > 0) {
            setData((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                content: {
                  ...prev.content,
                  // Append new events at the end (ASC order: oldest to newest)
                  events: [...prev.content.events, ...uniqueNewEvents],
                  total: result.content.total,
                },
              };
            });
            // Update latestEventId to the last (newest) event
            const lastEvent = uniqueNewEvents[uniqueNewEvents.length - 1];
            latestEventId.current = lastEvent.id;
          }
        }
      } catch {
        // Silently fail on poll errors
      }
    };

    const interval = setInterval(pollForNewMessages, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [token, error, data]);

  const loadContent = async (reset = false) => {
    if (!token) return;

    try {
      setLoading(true);
      const newOffset = reset ? 0 : offset;
      const result = await api.getSharedContent(token, limit, newOffset);

      if (reset) {
        setData(result);
        // Track the latest event ID for polling (last element with ASC order is newest)
        if (result.content.events.length > 0) {
          latestEventId.current =
            result.content.events[result.content.events.length - 1].id;
        }
      } else if (data) {
        const combinedEvents = [
          ...data.content.events,
          ...result.content.events,
        ];
        setData({
          ...result,
          content: {
            ...result.content,
            events: combinedEvents,
          },
        });
        // Update latestEventId to the newest event (last in ASC order)
        if (combinedEvents.length > 0) {
          latestEventId.current = combinedEvents[combinedEvents.length - 1].id;
        }
      }

      setHasMore(result.content.events.length === limit);
      setOffset(newOffset + result.content.events.length);
      setError(null);
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes("auth_required")) {
          setError("This share requires you to be logged in to view.");
        } else if (err.message.includes("not_granted")) {
          setError("You do not have access to this share.");
        } else if (err.message.includes("share_expired")) {
          setError("This share link has expired.");
        } else if (err.message.includes("share_revoked")) {
          setError("This share has been revoked by the owner.");
        } else {
          setError(err.message);
        }
      } else {
        setError("Failed to load shared content");
      }
    } finally {
      setLoading(false);
    }
  };

  const getScopeLabel = (scopeType: string) => {
    switch (scopeType) {
      case "all":
        return "All Messages";
      case "message":
        return "Single Message";
      case "session":
        return "Session";
      case "repository":
        return "Repository";
      case "conversation":
        return "Conversation";
      default:
        return scopeType;
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto py-12">
        <div className="bg-red-900/20 border border-red-500 rounded-lg p-6 text-center">
          <svg
            className="w-12 h-12 text-red-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h2 className="text-xl font-semibold text-red-400 mb-2">
            Unable to access share
          </h2>
          <p className="text-gray-400">{error}</p>
          <Link
            to="/"
            className="inline-block mt-4 px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
          >
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Share not found</p>
      </div>
    );
  }

  const { share, content } = data;

  // Check if this is a session share and last message was within the last hour
  const isSessionShare = share.scope_type === "session";
  const lastMessageTime =
    content.events.length > 0 && content.events[0].event_timestamp
      ? parseISO(content.events[0].event_timestamp)
      : null;
  const isRecentSession =
    isSessionShare && lastMessageTime
      ? differenceInMinutes(new Date(), lastMessageTime) < 60
      : false;

  const handleSendMessage = () => {
    alert("Not yet implemented");
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Unified Share Header */}
      <div className="bg-gradient-to-b from-gray-900 to-gray-900/50 rounded-lg border border-purple-500/30 overflow-hidden">
        {/* Header Info */}
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white mb-2">
                {share.title || "Shared Conversation"}
              </h1>
              {share.description && (
                <p className="text-gray-400 mb-4">{share.description}</p>
              )}
              <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-gray-400">
                <div className="flex items-center space-x-2">
                  {share.owner.avatar_url && (
                    <img
                      src={share.owner.avatar_url}
                      alt={share.owner.username}
                      className="w-5 h-5 rounded-full"
                    />
                  )}
                  <span>Shared by @{share.owner.username}</span>
                </div>
                <span className="text-gray-600">|</span>
                <span>{getScopeLabel(share.scope_type)}</span>
                <span className="text-gray-600">|</span>
                <span>{format(parseISO(share.created_at), "MMM d, yyyy")}</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="bg-purple-600/20 text-purple-400 px-3 py-1 rounded text-sm">
                {content.total} messages
              </div>
              {/* Compact Claude Code Share */}
              <button
                onClick={copyPrompt}
                className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors ${
                  copied
                    ? "bg-green-600/20 text-green-400"
                    : "bg-purple-600/20 text-purple-400 hover:bg-purple-600/30"
                }`}
                title={agentPrompt}
              >
                <svg
                  className="w-3 h-3"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
                {copied ? "Copied!" : "Share with Claude Code"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Messages - already in chronological order (ASC) from API */}
      <div className="space-y-4">
        {content.events.map((event) => (
          <MessageCard
            key={event.id}
            event={event}
            userAvatar={share.owner.avatar_url}
          />
        ))}

        {content.events.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400">No messages in this share</p>
          </div>
        )}

        {/* Send Message Section - only show if session is recent */}
        {isRecentSession && (
          <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Send message to this session
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm text-gray-300 placeholder-gray-500 focus:outline-none focus:border-purple-500"
              />
              <button
                onClick={handleSendMessage}
                className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-500 transition-colors"
              >
                Send
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              This message will be made available, but must be accepted and is
              not guaranteed to be used. [IN DEVELOPMENT]
            </p>
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        )}

        {hasMore && !loading && content.events.length > 0 && (
          <div className="flex justify-center">
            <button
              onClick={() => loadContent()}
              className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Load More
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-8 border-t border-gray-800">
        <p className="text-gray-500 text-sm">
          Powered by{" "}
          <a
            href="https://vibecheck.wanderingstan.com"
            className="text-purple-400 hover:underline"
          >
            Vibe-Check
          </a>
        </p>
      </div>
    </div>
  );
}
